// Per-dialect synthesizer for CREATE / ALTER / DROP TRIGGER (v2 Slice D6).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D6.
//
// Shell-only — `bodyText` is opaque verbatim text the resolver does not
// re-parse. The synthesizer emits the engine-specific prologue keywords and
// concatenates `bodyText` byte-for-byte. pgsql is the exception: pgsql
// triggers reference a separately-defined function via
// `EXECUTE FUNCTION pgsqlExecuteFunction()`, and `bodyText` is unused on pgsql.
//
// Per-engine surface:
//   - mssql:  CREATE [OR ALTER] TRIGGER <name> ON <target>
//             {AFTER|INSTEAD OF} <events> AS <body>
//             — no BEFORE; statement-level only
//   - pgsql:  CREATE [OR REPLACE] TRIGGER <name>
//             {BEFORE|AFTER|INSTEAD OF} <events> ON <target>
//             [FOR EACH {ROW|STATEMENT}] [WHEN (cond)] EXECUTE FUNCTION <fn>
//   - mysql:  CREATE TRIGGER <name> {BEFORE|AFTER} <event> ON <target>
//             FOR EACH ROW <body>
//             — single event only; no INSTEAD OF
//   - oracle: CREATE [OR REPLACE] [EDITIONABLE|NONEDITIONABLE] TRIGGER <name>
//             {BEFORE|AFTER|INSTEAD OF} <events> ON <target>
//             [FOR EACH ROW] <body>
//   - sqlite: CREATE [TEMP] TRIGGER <name>
//             {BEFORE|AFTER|INSTEAD OF} <event> ON <target>
//             [FOR EACH ROW] [WHEN cond] <body>
//             — single event only

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { TriggerChange, TriggerEvent, TriggerShellDef, TriggerTiming } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function nameText(engine: SqlEngine, name: QualifiedName): string {
  if (name.raw && name.raw.length > 0) return name.raw;
  const bare = maybeQuoteIdent(engine, name.name, name.quoted);
  if (name.schema) {
    const schema = maybeQuoteIdent(engine, name.schema, name.quoted);
    return `${schema}.${bare}`;
  }
  return bare;
}

function rejectField(engine: SqlEngine, op: string, field: string): never {
  throw new Error(`applyDdlEdit: ${engine} ${op} does not accept '${field}'`);
}

function unsupported(engine: SqlEngine, op: string, hint: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${hint}`,
  );
}

function timingKeyword(timing: TriggerTiming): string {
  if (timing === 'before') return 'BEFORE';
  if (timing === 'after') return 'AFTER';
  return 'INSTEAD OF';
}

function eventKeyword(ev: TriggerEvent): string {
  if (ev === 'insert') return 'INSERT';
  if (ev === 'update') return 'UPDATE';
  return 'DELETE';
}

/** postgres + oracle separate multiple trigger events with OR
 *  (`BEFORE INSERT OR UPDATE`) — a comma is a hard syntax error there
 *  (pg: `syntax error at or near ","`; oracle: ORA-00969), verified against
 *  live engines. mssql uses the documented comma form (`AFTER INSERT, UPDATE`).
 *  mysql + sqlite are single-event engines (rejected above) and never
 *  reach a multi-event list. */
function eventList(events: readonly TriggerEvent[], separator: ', ' | ' OR '): string {
  return events.map(eventKeyword).join(separator);
}

function validateBody(t: TriggerShellDef, op: string): void {
  if (t.bodyText.trim().length === 0) {
    throw new Error(
      `applyDdlEdit: ${op} requires a non-empty bodyText (the trigger body)`,
    );
  }
}

// ─── CREATE TRIGGER ───────────────────────────────────────────────────────

export function synthesizeCreateTrigger(
  engine: SqlEngine,
  trigger: TriggerShellDef,
): string {
  if (trigger.events.length === 0) {
    throw new Error(`applyDdlEdit: create-trigger requires at least one event`);
  }
  if (trigger.oracleEditionable && trigger.oracleNonEditionable) {
    throw new Error(
      `applyDdlEdit: oracle create-trigger: 'oracleEditionable' and `
        + `'oracleNonEditionable' are mutually exclusive`,
    );
  }
  if (engine === 'mssql') return synthMssqlTrigger(trigger);
  if (engine === 'postgres') return synthPgsqlTrigger(trigger);
  if (engine === 'mysql') return synthMysqlTrigger(trigger);
  if (engine === 'oracle') return synthOracleTrigger(trigger);
  return synthSqliteTrigger(trigger);
}

function rejectNonMssqlFields(engine: SqlEngine, t: TriggerShellDef): void {
  if (t.mssqlOrAlter) rejectField(engine, 'create-trigger', 'mssqlOrAlter');
}

function rejectNonOracleFields(engine: SqlEngine, t: TriggerShellDef): void {
  if (t.oracleEditionable) {
    rejectField(engine, 'create-trigger', 'oracleEditionable');
  }
  if (t.oracleNonEditionable) {
    rejectField(engine, 'create-trigger', 'oracleNonEditionable');
  }
}

function rejectNonSqliteFields(engine: SqlEngine, t: TriggerShellDef): void {
  if (t.sqliteTemporary) rejectField(engine, 'create-trigger', 'sqliteTemporary');
}

function rejectNonPgsqlFields(engine: SqlEngine, t: TriggerShellDef): void {
  if (t.pgsqlExecuteFunction) {
    rejectField(engine, 'create-trigger', 'pgsqlExecuteFunction');
  }
}

function synthMssqlTrigger(t: TriggerShellDef): string {
  validateBody(t, 'create-trigger');
  if (t.orReplace) rejectField('mssql', 'create-trigger', 'orReplace');
  if (t.timing === 'before') {
    unsupported('mssql', 'create-trigger BEFORE',
      `mssql triggers fire AFTER or INSTEAD OF only — there is no BEFORE `
        + `trigger form. Convert to AFTER + post-validation logic, or to an `
        + `INSTEAD OF trigger if you need to suppress the operation.`);
  }
  if (t.granularity) rejectField('mssql', 'create-trigger', 'granularity');
  if (t.whenCondition) rejectField('mssql', 'create-trigger', 'whenCondition');
  rejectNonOracleFields('mssql', t);
  rejectNonSqliteFields('mssql', t);
  rejectNonPgsqlFields('mssql', t);
  let s = t.mssqlOrAlter ? 'CREATE OR ALTER TRIGGER ' : 'CREATE TRIGGER ';
  s += `${nameText('mssql', t.name)} ON ${nameText('mssql', t.target)} `;
  s += `${timingKeyword(t.timing)} ${eventList(t.events, ', ')}`;
  s += ` AS ${t.bodyText.trim()}`;
  return s;
}

function synthPgsqlTrigger(t: TriggerShellDef): string {
  if (!t.pgsqlExecuteFunction) {
    throw new Error(
      `applyDdlEdit: postgres create-trigger requires 'pgsqlExecuteFunction' `
        + `— pgsql triggers reference a separately-defined function via `
        + `EXECUTE FUNCTION fn().`,
    );
  }
  rejectNonMssqlFields('postgres', t);
  rejectNonOracleFields('postgres', t);
  rejectNonSqliteFields('postgres', t);
  let s = t.orReplace ? 'CREATE OR REPLACE TRIGGER ' : 'CREATE TRIGGER ';
  s += `${nameText('postgres', t.name)} `;
  s += `${timingKeyword(t.timing)} ${eventList(t.events, ' OR ')} `;
  s += `ON ${nameText('postgres', t.target)}`;
  if (t.granularity === 'row') s += ' FOR EACH ROW';
  else if (t.granularity === 'statement') s += ' FOR EACH STATEMENT';
  if (t.whenCondition) s += ` WHEN (${t.whenCondition})`;
  s += ` EXECUTE FUNCTION ${t.pgsqlExecuteFunction}`;
  return s;
}

function synthMysqlTrigger(t: TriggerShellDef): string {
  validateBody(t, 'create-trigger');
  if (t.orReplace) rejectField('mysql', 'create-trigger', 'orReplace');
  if (t.timing === 'instead-of') {
    unsupported('mysql', 'create-trigger INSTEAD OF',
      `mysql has no INSTEAD OF triggers. Use BEFORE / AFTER on a base table, `
        + `or implement an updatable view with a trigger on the underlying tables.`);
  }
  if (t.events.length > 1) {
    unsupported('mysql', 'create-trigger multi-event',
      `mysql triggers fire on a single event only. Split the trigger into `
        + `per-event triggers (one per event in 'events').`);
  }
  if (t.granularity && t.granularity !== 'row') {
    rejectField('mysql', 'create-trigger', 'granularity');
  }
  if (t.whenCondition) rejectField('mysql', 'create-trigger', 'whenCondition');
  rejectNonOracleFields('mysql', t);
  rejectNonSqliteFields('mysql', t);
  rejectNonPgsqlFields('mysql', t);
  let s = `CREATE TRIGGER ${nameText('mysql', t.name)} `;
  s += `${timingKeyword(t.timing)} ${eventKeyword(t.events[0]!)} `;
  s += `ON ${nameText('mysql', t.target)} FOR EACH ROW `;
  s += t.bodyText.trim();
  return s;
}

function synthOracleTrigger(t: TriggerShellDef): string {
  validateBody(t, 'create-trigger');
  if (t.granularity && t.granularity !== 'row') {
    rejectField('oracle', 'create-trigger', 'granularity');
  }
  if (t.whenCondition) rejectField('oracle', 'create-trigger', 'whenCondition');
  rejectNonMssqlFields('oracle', t);
  rejectNonSqliteFields('oracle', t);
  rejectNonPgsqlFields('oracle', t);
  let s = t.orReplace ? 'CREATE OR REPLACE ' : 'CREATE ';
  if (t.oracleEditionable) s += 'EDITIONABLE ';
  if (t.oracleNonEditionable) s += 'NONEDITIONABLE ';
  s += `TRIGGER ${nameText('oracle', t.name)} `;
  s += `${timingKeyword(t.timing)} ${eventList(t.events, ' OR ')} `;
  s += `ON ${nameText('oracle', t.target)}`;
  if (t.granularity === 'row') s += ' FOR EACH ROW';
  s += ` ${t.bodyText.trim()}`;
  return s;
}

function synthSqliteTrigger(t: TriggerShellDef): string {
  validateBody(t, 'create-trigger');
  if (t.orReplace) rejectField('sqlite', 'create-trigger', 'orReplace');
  if (t.events.length > 1) {
    unsupported('sqlite', 'create-trigger multi-event',
      `sqlite triggers fire on a single event only. Split the trigger into `
        + `per-event triggers (one per event in 'events').`);
  }
  rejectNonMssqlFields('sqlite', t);
  rejectNonOracleFields('sqlite', t);
  rejectNonPgsqlFields('sqlite', t);
  let s = t.sqliteTemporary ? 'CREATE TEMP TRIGGER ' : 'CREATE TRIGGER ';
  s += `${nameText('sqlite', t.name)} `;
  s += `${timingKeyword(t.timing)} ${eventKeyword(t.events[0]!)} `;
  s += `ON ${nameText('sqlite', t.target)}`;
  if (t.granularity === 'row') s += ' FOR EACH ROW';
  if (t.whenCondition) s += ` WHEN ${t.whenCondition}`;
  s += ` ${t.bodyText.trim()}`;
  return s;
}

// ─── ALTER TRIGGER (enable / disable) ─────────────────────────────────────

export function synthesizeAlterTrigger(
  engine: SqlEngine,
  triggerName: QualifiedName,
  target: QualifiedName | undefined,
  change: TriggerChange,
): string {
  if (engine === 'mysql') {
    unsupported('mysql', 'alter-trigger',
      `mysql has no ALTER TRIGGER. Drop and recreate via op:'drop-trigger' + `
        + `op:'create-trigger' to change a trigger.`);
  }
  if (engine === 'sqlite') {
    unsupported('sqlite', 'alter-trigger',
      `sqlite has no ALTER TRIGGER. Drop and recreate via op:'drop-trigger' + `
        + `op:'create-trigger' to change a trigger.`);
  }
  const verb = change.enabled ? 'ENABLE' : 'DISABLE';
  const tn = nameText(engine, triggerName);
  if (engine === 'mssql') {
    if (!target) {
      throw new Error(
        `applyDdlEdit: mssql alter-trigger requires 'target' — `
          + `T-SQL syntax is '{ENABLE|DISABLE} TRIGGER name ON target'.`,
      );
    }
    return `${verb} TRIGGER ${tn} ON ${nameText('mssql', target)}`;
  }
  if (engine === 'postgres') {
    if (!target) {
      throw new Error(
        `applyDdlEdit: postgres alter-trigger requires 'target' — `
          + `pgsql syntax is 'ALTER TABLE target {ENABLE|DISABLE} TRIGGER name'.`,
      );
    }
    return `ALTER TABLE ${nameText('postgres', target)} ${verb} TRIGGER ${tn}`;
  }
  // oracle
  if (target) rejectField('oracle', 'alter-trigger', 'target');
  return `ALTER TRIGGER ${tn} ${verb}`;
}

// ─── DROP TRIGGER ─────────────────────────────────────────────────────────

export function synthesizeDropTrigger(
  engine: SqlEngine,
  triggerName: QualifiedName,
  target: QualifiedName | undefined,
  ifExists: boolean,
  cascade: boolean,
): string {
  if (engine === 'postgres') {
    if (!target) {
      throw new Error(
        `applyDdlEdit: postgres drop-trigger requires 'target' — `
          + `pgsql syntax is 'DROP TRIGGER name ON target'.`,
      );
    }
    let s = 'DROP TRIGGER ';
    if (ifExists) s += 'IF EXISTS ';
    s += `${nameText('postgres', triggerName)} ON ${nameText('postgres', target)}`;
    if (cascade) s += ' CASCADE';
    return s;
  }
  if (target) rejectField(engine, 'drop-trigger', 'target');
  if (cascade) rejectField(engine, 'drop-trigger', 'cascade');
  let s = 'DROP TRIGGER ';
  if (ifExists) {
    if (engine === 'oracle') {
      throw new Error(
        `applyDdlEdit: oracle drop-trigger does not accept 'ifExists' — `
          + `oracle DROP TRIGGER has no IF EXISTS keyword. Wrap in PL/SQL `
          + `with USER_TRIGGERS lookup at the consumer level.`,
      );
    }
    s += 'IF EXISTS ';
  }
  s += nameText(engine, triggerName);
  return s;
}
